import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  IconButton,
  Tooltip,
  Button,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import {
  KeyboardArrowDown,
  Phone,
  Chat,
  Description,
  ChevronLeft,
  CalendarToday
} from '@mui/icons-material';
import DocumentationDrawer from '../../components/DocumentationDrawer';
import DocumentationContentPanel from '../../components/DocumentationContentPanel';
import StepperDocumentation from '../../components/StepperDocumentation';
import { DocumentationSection, SDKOption, StepperData, DocumentationKitType } from '../../types/documentation';
import documentationData from '../../data/documentationOptions.json';

const KIT_TYPES: DocumentationKitType[] = ['sdk', 'uikit'];
const DEFAULT_LANGUAGE = 'react';

const Documentation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { sdk, sectionId, kitType: kitTypeParam } = useParams<{ sdk?: string; sectionId?: string; kitType?: string }>();

  // Determine kit type ('sdk' or 'uikit') from URL; defaults to 'uikit' to preserve existing behavior.
  const getInitialKitType = (): DocumentationKitType => {
    if (kitTypeParam && KIT_TYPES.includes(kitTypeParam as DocumentationKitType)) {
      return kitTypeParam as DocumentationKitType;
    }
    const path = location.pathname;
    if (path.includes('/sdk/')) return 'sdk';
    if (path.includes('/uikit/')) return 'uikit';
    return 'uikit';
  };

  // Determine initial mode based on current route
  const getInitialMode = (): 'documentation' | 'calls' | 'chat' | 'scheduleConsultation' => {
    const path = location.pathname;
    if (path.includes('/calls')) return 'calls';
    if (path.includes('/chat')) return 'chat';
    if (path.includes('/schedule-consultation')) return 'scheduleConsultation';
    return 'documentation';
  };

  const getInitialLanguage = (): string => {
    if (sdk) return sdk;
    const path = location.pathname;
    const options = [
      ...((documentationData as any).sdkOptions || []),
      ...((documentationData as any).uiKitOptions || [])
    ];
    // Prefer longer ids first so "react-native" is not matched as "react".
    const sorted = [...options].sort((a: SDKOption, b: SDKOption) => b.id.length - a.id.length);
    const matched = sorted.find((option: SDKOption) => path.includes(`/${option.id}`));
    return matched?.id || DEFAULT_LANGUAGE;
  };

  const [drawerOpen, setDrawerOpen] = useState<boolean>(true);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('getting-started');
  const [documentationSections, setDocumentationSections] = useState<DocumentationSection[]>([]);
  const [currentMode, setCurrentMode] = useState<'documentation' | 'calls' | 'chat' | 'scheduleConsultation'>(getInitialMode());
  const [currentKitType, setCurrentKitType] = useState<DocumentationKitType>(getInitialKitType());
  const [kitMenuAnchorEl, setKitMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [modeMenuAnchorEl, setModeMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSdk, setSelectedSdk] = useState<string>(getInitialLanguage());
  const [sdkOptions, setSdkOptions] = useState<SDKOption[]>([]);
  const [showStepper, setShowStepper] = useState<boolean>(false);
  const [stepperData, setStepperData] = useState<StepperData | null>(null);

  // Returns the list of options to render in the drawer selector based on current kitType.
  const getOptionsForKitType = (kitType: DocumentationKitType): SDKOption[] => {
    const data = documentationData as any;
    if (kitType === 'sdk') return data.sdkOptions || [];
    return data.uiKitOptions || [];
  };

  // Returns the section data tree for the current kitType + mode.
  const getSectionsForKitTypeAndMode = (
    kitType: DocumentationKitType,
    mode: 'calls' | 'chat' | 'scheduleConsultation'
  ): any[] => {
    const data = documentationData as any;
    const treeKey = kitType === 'sdk' ? 'sdkSections' : 'uiKitSections';
    return data[treeKey]?.[mode] || [];
  };

  // Builds a URL for the current kitType + mode + sdk + section.
  const buildRoute = (
    mode: 'documentation' | 'calls' | 'chat' | 'scheduleConsultation',
    kitType: DocumentationKitType,
    sdkId: string,
    sectionIdValue?: string
  ): string => {
    if (mode === 'documentation') {
      return sectionIdValue ? `/documentation/${sectionIdValue}` : '/documentation/getting-started';
    }
    const modeSegment = mode === 'scheduleConsultation' ? 'schedule-consultation' : mode;
    const base = `/documentation/${kitType}/${modeSegment}/${sdkId}`;
    return sectionIdValue ? `${base}/${sectionIdValue}` : base;
  };

  useEffect(() => {
    setSdkOptions(getOptionsForKitType(currentKitType));
    updateSections();
  }, []);

  // Handle route changes
  useEffect(() => {
    const newMode = getInitialMode();
    const newLanguage = getInitialLanguage();
    const newKitType = getInitialKitType();
    if (newMode !== currentMode) {
      setCurrentMode(newMode);
    }
    if (newLanguage !== selectedSdk) {
      setSelectedSdk(newLanguage);
    }
    if (newKitType !== currentKitType) {
      setCurrentKitType(newKitType);
    }
  }, [location.pathname]);

  // Handle section ID changes from URL (for browser back/forward navigation)
  useEffect(() => {
    if (sectionId && sectionId !== selectedSectionId) {
      setSelectedSectionId(sectionId);
    }
  }, [sectionId]);

  const transformSDKSectionToDocumentationSection = (sdkSection: any, selectedSdk: string): DocumentationSection | null => {
    const sdkContent = sdkSection.content[selectedSdk];
    
    // Transform subsections if they exist
    let transformedSubSections = undefined;
    if (sdkSection.subSections && Array.isArray(sdkSection.subSections)) {
      transformedSubSections = sdkSection.subSections
        .map((subSection: any) => {
          const subSdkContent = subSection.content[selectedSdk];
          if (!subSdkContent) {
            return null;
          }
          return {
            id: subSection.id,
            title: subSection.title,
            content: subSdkContent
          };
        })
        .filter((subSection: any) => subSection !== null);
    }
    
    // Return null if the selected SDK doesn't have content for this section
    if (!sdkContent && (!transformedSubSections || transformedSubSections.length === 0)) {
      return null;
    }
    
    return {
      id: sdkSection.id,
      title: sdkSection.title,
      icon: sdkSection.icon,
      content: sdkContent || {},
      subSections: transformedSubSections
    };
  };

  const updateSections = () => {
    const data = documentationData as any;

    const applyKitTypeMode = (mode: 'calls' | 'chat' | 'scheduleConsultation') => {
      const rawSections = getSectionsForKitTypeAndMode(currentKitType, mode);
      const transformed = rawSections
        .map((section: any) => transformSDKSectionToDocumentationSection(section, selectedSdk))
        .filter((section: DocumentationSection | null) => section !== null) as DocumentationSection[];
      setDocumentationSections(transformed);

      if (transformed.length > 0) {
        const sectionExists = !!sectionId && transformed.some(s =>
          s.id === sectionId || s.subSections?.some(sub => sub.id === sectionId)
        );
        if (sectionExists && sectionId) {
          setSelectedSectionId(sectionId);
        } else {
          const firstSection = transformed[0];
          const firstId = firstSection.subSections && firstSection.subSections.length > 0
            ? firstSection.subSections[0].id
            : firstSection.id;
          setSelectedSectionId(firstId);
          navigate(buildRoute(mode, currentKitType, selectedSdk, firstId), { replace: true });
        }
      } else {
        setSelectedSectionId('');
      }
    };

    switch (currentMode) {
      case 'calls':
        applyKitTypeMode('calls');
        break;
      case 'chat':
        applyKitTypeMode('chat');
        break;
      case 'scheduleConsultation':
        applyKitTypeMode('scheduleConsultation');
        break;
      default:
        setDocumentationSections(data.sections);
        const defaultSectionId = sectionId || 'getting-started';
        setSelectedSectionId(defaultSectionId);
        // Update URL with section ID for documentation mode
        if (!sectionId) {
          navigate(`/documentation/${defaultSectionId}`, { replace: true });
        }
    }
  };

  useEffect(() => {
    setSdkOptions(getOptionsForKitType(currentKitType));
  }, [currentKitType]);

  useEffect(() => {
    updateSections();
  }, [currentMode, selectedSdk, currentKitType]);

  // Keep stepper data in sync with kitType / mode / sdk changes while stepper is open.
  useEffect(() => {
    if (!showStepper) return;
    const stepper = getStepperDataFor(currentKitType, currentMode, selectedSdk);
    if (stepper) {
      setStepperData(stepper);
    } else {
      setShowStepper(false);
      setStepperData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKitType]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSectionSelect = (newSectionId: string) => {
    setSelectedSectionId(newSectionId);
    navigate(buildRoute(currentMode, currentKitType, selectedSdk, newSectionId));

    if (window.innerWidth < 900) {
      setDrawerOpen(false);
    }
  };

  // Returns the stepper data for the current kitType + mode + sdk, with a fallback
  // to legacy non-kitType structure for backward compatibility.
  const getStepperDataFor = (
    kitType: DocumentationKitType,
    mode: 'documentation' | 'calls' | 'chat' | 'scheduleConsultation',
    sdkId: string
  ): StepperData | null => {
    if (mode === 'documentation') return null;
    const data = documentationData as any;
    const root = data.gettingStarted;
    if (!root) return null;
    // New shape: gettingStarted[kitType][mode][sdk]
    if (root[kitType] && root[kitType][mode] && root[kitType][mode][sdkId]) {
      return root[kitType][mode][sdkId] as StepperData;
    }
    // Legacy shape: gettingStarted[mode][sdk]
    if (root[mode] && root[mode][sdkId]) {
      return root[mode][sdkId] as StepperData;
    }
    return null;
  };

  const handleSdkChange = (sdkId: string) => {
    setSelectedSdk(sdkId);
    
    // Update stepper data if stepper is open - use current mode and new SDK
    if (showStepper) {
      const stepper = getStepperDataFor(currentKitType, currentMode, sdkId);
      if (stepper) {
        setStepperData(stepper);
      }
    }
    
    // Navigate to the appropriate route with the selected SDK and current section
    if (currentMode === 'documentation') {
      // No SDK selection for general documentation
      return;
    }
    navigate(buildRoute(currentMode, currentKitType, sdkId, selectedSectionId || undefined));
  };

  const handleKitMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setKitMenuAnchorEl(event.currentTarget);
  };

  const handleKitMenuClose = () => {
    setKitMenuAnchorEl(null);
  };

  const handleModeMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setModeMenuAnchorEl(event.currentTarget);
  };

  const handleModeMenuClose = () => {
    setModeMenuAnchorEl(null);
  };

  // Resolves the default language for a kit type (prefers react, else first option).
  const getDefaultLanguageForKitType = (kitType: DocumentationKitType): string => {
    const options = getOptionsForKitType(kitType);
    if (options.some(option => option.id === DEFAULT_LANGUAGE)) {
      return DEFAULT_LANGUAGE;
    }
    return options[0]?.id || DEFAULT_LANGUAGE;
  };

  // Keeps the current language when it exists for the target kit; otherwise uses default.
  const resolveLanguageForKitType = (
    kitType: DocumentationKitType,
    preferredLanguage: string
  ): string => {
    const options = getOptionsForKitType(kitType);
    if (options.some(option => option.id === preferredLanguage)) {
      return preferredLanguage;
    }
    return getDefaultLanguageForKitType(kitType);
  };

  // Checks whether a section ID exists in the transformed docs for kit + mode + language.
  const sectionExistsForSelection = (
    kitType: DocumentationKitType,
    mode: 'calls' | 'chat' | 'scheduleConsultation',
    language: string,
    sectionIdValue?: string
  ): boolean => {
    if (!sectionIdValue) return false;
    const rawSections = getSectionsForKitTypeAndMode(kitType, mode);
    const transformed = rawSections
      .map((section: any) => transformSDKSectionToDocumentationSection(section, language))
      .filter((section: DocumentationSection | null) => section !== null) as DocumentationSection[];

    return transformed.some(section =>
      section.id === sectionIdValue ||
      section.subSections?.some(sub => sub.id === sectionIdValue)
    );
  };

  // Switches between UI Kit and SDK. Keeps language/section when they exist; otherwise defaults.
  const handleKitTypeChange = (kitType: DocumentationKitType) => {
    handleKitMenuClose();
    if (kitType === currentKitType && currentMode !== 'documentation') {
      return;
    }

    const targetMode = currentMode === 'documentation' ? 'calls' : currentMode;
    const targetLanguage = resolveLanguageForKitType(kitType, selectedSdk);
    const keepSection = sectionExistsForSelection(
      kitType,
      targetMode,
      targetLanguage,
      selectedSectionId
    );

    setCurrentKitType(kitType);
    setSelectedSdk(targetLanguage);
    setCurrentMode(targetMode);
    setDrawerOpen(true);
    setShowStepper(false);
    setStepperData(null);

    navigate(buildRoute(
      targetMode,
      kitType,
      targetLanguage,
      keepSection ? selectedSectionId : undefined
    ));
  };

  const handleGettingStartedClick = () => {
    const stepper = getStepperDataFor(currentKitType, currentMode, selectedSdk);
    if (stepper) {
      setStepperData(stepper);
      setShowStepper(true);
    }
  };

  const handleCloseStepper = () => {
    setShowStepper(false);
    setStepperData(null);
  };

  const handleModeChange = (mode: 'documentation' | 'calls' | 'chat' | 'scheduleConsultation') => {
    handleModeMenuClose();
    setDrawerOpen(true);

    if (mode === 'documentation') {
      setCurrentMode(mode);
      if (showStepper) {
        setShowStepper(false);
        setStepperData(null);
      }
      navigate(buildRoute(mode, currentKitType, selectedSdk));
      return;
    }

    // Keep current language when available for this kit; otherwise fall back to default.
    const targetLanguage = resolveLanguageForKitType(currentKitType, selectedSdk);
    const keepSection = sectionExistsForSelection(
      currentKitType,
      mode,
      targetLanguage,
      selectedSectionId
    );

    setCurrentMode(mode);
    setSelectedSdk(targetLanguage);

    if (showStepper) {
      const stepper = getStepperDataFor(currentKitType, mode, targetLanguage);
      if (stepper) {
        setStepperData(stepper);
      } else {
        setShowStepper(false);
        setStepperData(null);
      }
    }

    navigate(buildRoute(
      mode,
      currentKitType,
      targetLanguage,
      keepSection ? selectedSectionId : undefined
    ));
  };

  const getKitTypeLabel = (kitType: DocumentationKitType) =>
    kitType === 'sdk' ? 'SDK' : 'UI Kit';

  const getSelectedContent = () => {
    // First, check if the selected ID is a main section
    const mainSection = documentationSections.find(section => section.id === selectedSectionId);
    if (mainSection && !mainSection.subSections) {
      return mainSection;
    }

    // If not found as a main section, search in subsections
    for (const section of documentationSections) {
      if (section.subSections) {
        const subSection = section.subSections.find(sub => sub.id === selectedSectionId);
        if (subSection) {
          return {
            id: subSection.id,
            title: subSection.title,
            icon: section.icon,
            content: subSection.content
          };
        }
      }
    }

    return mainSection;
  };

  const selectedSection = getSelectedContent();

  const getCurrentModeTitle = () => {
    const kitLabel = getKitTypeLabel(currentKitType);
    switch (currentMode) {
      case 'calls': return `${kitLabel} - Calls`;
      case 'chat': return `${kitLabel} - Chat`;
      case 'scheduleConsultation': return `${kitLabel} - Schedule Consultation`;
      default: return 'Sampark Documentation';
    }
  };

  const getCurrentModeLabel = () => {
    switch (currentMode) {
      case 'calls': return 'Calls';
      case 'chat': return 'Chat';
      case 'scheduleConsultation': return 'Schedule Consultation';
      default: return 'Documentation';
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          px: 2,
          zIndex: 1200
        }}
      >
        <Tooltip title="Go back">
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            <ChevronLeft sx={{ cursor: 'pointer' }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ typography: 'h6', fontWeight: 600, color: '#1976d2', cursor: 'pointer' }} onClick={handleDrawerToggle} >
          {getCurrentModeTitle()}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, color: '#555' }}>Sections :</Typography>
            <Button
              onClick={handleKitMenuClick}
              endIcon={<KeyboardArrowDown />}
              sx={{
                color: '#1976d2',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              {getKitTypeLabel(currentKitType)}
            </Button>
            <Menu
              anchorEl={kitMenuAnchorEl}
              open={Boolean(kitMenuAnchorEl)}
              onClose={handleKitMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                selected={currentKitType === 'uikit'}
                onClick={() => handleKitTypeChange('uikit')}
                sx={{ minWidth: 120 }}
              >
                <Typography>UI Kit</Typography>
              </MenuItem>
              <MenuItem
                selected={currentKitType === 'sdk'}
                onClick={() => handleKitTypeChange('sdk')}
                sx={{ minWidth: 120 }}
              >
                <Typography>SDK</Typography>
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, color: '#555' }}>Modules :</Typography>
            <Button
              onClick={handleModeMenuClick}
              endIcon={<KeyboardArrowDown />}
              sx={{
                color: '#1976d2',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              {getCurrentModeLabel()}
            </Button>
            <Menu
              anchorEl={modeMenuAnchorEl}
              open={Boolean(modeMenuAnchorEl)}
              onClose={handleModeMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => handleModeChange('documentation')}
                sx={{ minWidth: 120 }}
              >
                <Description sx={{ mr: 1, fontSize: 20 }} />
                <Typography>Documentation</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleModeChange('calls')}
                sx={{ minWidth: 120 }}
              >
                <Phone sx={{ mr: 1, fontSize: 20 }} />
                <Typography>Calls</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleModeChange('chat')}
                sx={{ minWidth: 120 }}
              >
                <Chat sx={{ mr: 1, fontSize: 20 }} />
                <Typography>Chat</Typography>
              </MenuItem>
              <MenuItem
                onClick={() => handleModeChange('scheduleConsultation')}
                sx={{ minWidth: 120 }}
              >
                <CalendarToday sx={{ mr: 1, fontSize: 20 }} />
                <Typography>Schedule Consultation</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Box>
      {showStepper && stepperData ? (
        <StepperDocumentation data={stepperData} onClose={handleCloseStepper} />
      ) : (
        <>
          {drawerOpen && <DocumentationDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sections={documentationSections}
            selectedSectionId={selectedSectionId}
            onSectionSelect={handleSectionSelect}
            sdkOptions={sdkOptions}
            selectedSdk={selectedSdk}
            onSdkChange={handleSdkChange}
            showSdkSelector={currentMode === 'calls' || currentMode === 'chat' || currentMode === 'scheduleConsultation'}
            onGettingStartedClick={handleGettingStartedClick}
            kitType={currentKitType}
          />}
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              pt: '64px',
              minHeight: '100vh',
              width: '100%',
              overflow: 'hidden'
            }}
          >
            {documentationSections.length === 0 && (currentMode === 'calls' || currentMode === 'chat' || currentMode === 'scheduleConsultation') ? (
              <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
                <Box sx={{ typography: 'h5', color: '#666', mb: 2 }}>
                  No documentation available
                </Box>
                <Box sx={{ typography: 'body1', color: '#888' }}>
                  No documentation sections are available for {sdkOptions.find(sdk => sdk.id === selectedSdk)?.name} in the {currentMode} category.
                </Box>
              </Box>
            ) : selectedSection ? (
              <DocumentationContentPanel
                content={selectedSection.content}
                drawerOpen={drawerOpen}
              />
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Box sx={{ typography: 'h5' }}>Loading...</Box>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default Documentation;
